import React, { useEffect, useState, useContext } from "react";
import PandaProfile from "../../../../assets/PandaProfile.png";
import mountainAndPath from "../../../../assets/mountainAndPath.png";
import MountainMan from "../../../../assets/MountainMan.png";
import RectangleFrame from "../../../../assets/RectangleFrame.png";
import learningPathFlag from "../../../../assets/learningPathFlag.png";
import { FaLocationDot } from "react-icons/fa6";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../UserContext";
import { apiFetch } from "../../../../services/apiFetch";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const CareerProgressionJourney = ({ onComplete }) => {
  const { user } = useContext(UserContext);
  const USER_ID = user?.user_id;
  const handleStartClick = () => {
    // Call parent's onComplete if provided, otherwise do nothing
    if (onComplete) {
      onComplete();
    }
  };

  const navigate = useNavigate();
  const [hasModule1Completed, setHasModule1Completed] = useState(false);

  useEffect(() => {
    if (!USER_ID) return;

    const loadProgress = async () => {
      try {
        const coursesRes = await apiFetch(`${apiBaseURL}/courses`);
        const courses = await coursesRes.json();
        if (!Array.isArray(courses) || courses.length === 0) return;

        const firstCourse = courses[0];
        const progressRes = await apiFetch(
          `${apiBaseURL}/users/${USER_ID}/courses/${firstCourse.id}/progress`
        );
        const progress = await progressRes.json();
        if (!progress?.modules?.length) return;

        const module1 = progress.modules.find((m) => m.order === 1);
        if (!module1 || !module1.subsections?.length) return;

        const allCompleted = module1.subsections.every(
          (sub) => sub.completed || sub.status === "completed"
        );
        setHasModule1Completed(allCompleted);
      } catch (error) {
        console.error("Error checking Module 1 completion:", error);
      }
    };

    loadProgress();
  }, [USER_ID]);

  return (
    <section className="relative w-full bg-white overflow-hidden h-auto pt-6 pb-2">
      {/* Top Content */}
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-8">
          {/* Left Text */}
          <div className="max-w-4xl md:pl-5">
            <h1 className="text-3xl md:text-[34px] font-medium font-gotham text-[#000000] leading-tight">
              Welcome to Your Career Progression Journey
            </h1>
            <p className="mt-3 max-w-2xl text-base md:text-[17px] text-[#000000] font-gotham font-normal leading-6">
              This is your space to reflect, track, and visualise grow.
              Everything that matters to your career journey, in one place.
            </p>
          </div>

          {/* Right Avatar Placeholder */}
          <div className="shrink-0">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mr-6">
              <img
                src={PandaProfile}
                alt="User Avatar"
                className="w-16 h-16 md:h-20 md:w-20 rounded-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Journey Graphic Area */}
      <div className="relative mt-20 w-full">

        {/* Frames Container */}
        <div className="relative w-full h-[320px] md:h-[420px] flex items-end justify-between px-6">
          
          {/* Frame 1 */}
          <div className="flex flex-col items-center">
            <div className="w-full h-auto flex items-center justify-center">
              <img
                src={mountainAndPath}
                alt="Frame 1"
                className="w-full h-full object-contain"
              />
              <img
                src={MountainMan}
                alt="Frame 1"
                className="w-full h-[120px] min-w-[100px] object-contain absolute bottom-5 left-[7.5%] -translate-x-[630px]"
              />
            </div>
          </div>
          <div className="flex flex-col items-center">
          </div>
        </div>

        {/* Rectangle Below Frames */}
        <img
          src={RectangleFrame}
          alt="Rectangle Frame"
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] h-auto object-contain"
        />

        <img
          src={learningPathFlag}
          alt="Learning Path Flag"
          className="absolute bottom-[46.5%] left-[54.65%] w-10 h-12 md:w-16 md:h-16 object-contain"
        />

        <img
          src={learningPathFlag}
          alt="Learning Path Flag"
          className="absolute bottom-[87%] right-[14.22%] w-10 h-12 md:w-16 md:h-16 object-contain"
        />

        {/* Labels */}
        <div className="absolute left-[5%] bottom-[43%] text-sm text-[#4A4A4A]">
          <div className="flex items-center gap-2 relative">
            <span className="font-normal text-[#363636] font-gotham text-[10.71px]">YOU ARE HERE</span>
          </div>
          <FaLocationDot className="text-[#54B0AF] absolute top-7 left-1/3 text-2xl" />
        </div>

        <div className="absolute right-[7.5%] top-8 text-sm text-[#363636] text-right">
          <span className="font-normal font-gotham block">YOUR FUTURE</span>
          <span className="font-normal font-gotham">CAREER ROLE</span>
        </div>

        {/* Bottom Labels */}
        <div className="absolute left-0 right-0 -bottom-10 flex justify-between px-6 text-sm md:text-[10.71px] font-normal font-gotham text-[#363636]">
          <div className="flex justify-between w-full gap-2">

            {/* YOUR LEARNING PATH */}
            <div
              onClick={() => navigate("/learner/learning#learningPath")}
              className="
                w-[58%]
                border border-t-white border-b-black border-l-black border-r-black
                px-2 py-1
                text-blue-600 underline
                text-sm md:text-[10.71px]
                font-normal font-gotham text-[#363636]
                bg-transparent
                transition-all duration-300 ease-in-out
                hover:bg-[#54B0AF]
                hover:text-white
                hover:shadow-md
                cursor-pointer
              "
            >
              YOUR LEARNING PATH
            </div>

            {/* YOUR ACTION PLAN */}
            <div
              onClick={() => {
                if (hasModule1Completed) {
                  navigate("/learner/action-plan");
                }
              }}
              className={`
                w-[42%]
                border border-t-white border-b-black border-l-black border-r-black
                px-2 py-1
                text-blue-600 underline
                text-sm md:text-[10.71px]
                font-normal font-gotham text-[#363636]
                bg-transparent
                transition-all duration-300 ease-in-out
                ${hasModule1Completed ? "hover:bg-[#54B0AF] hover:text-white hover:shadow-md cursor-pointer" : "opacity-50 cursor-not-allowed"}
              `}
            >
              YOUR ACTION PLAN
            </div>

          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-20 mx-4">
        <button 
          onClick={handleStartClick} 
          className="flex items-center gap-4 bg-[#54B0AF] hover:bg-teal-600 transition text-white px-6 py-2.5 rounded-[22px] font-gotham font-medium text-lg md:text-[21px] shadow-md"
        >
          Start Your Journey
          <span className="bg-white rounded-full p-2">
            <ChevronRight className="w-4 h-4 text-black" />
          </span>
        </button>
      </div>
    </section>
  );
};

export default CareerProgressionJourney;
